import React, {useState, useContext} from 'react';
import PropTypes from 'prop-types';
import {compose, graphql} from 'react-apollo';

import Modal from '@homeaway/react-modal';
import {ButtonPrimary} from '@homeaway/react-buttons';
import {Waypoint} from '@homeaway/react-unveil';

import {ModifyStayBooking as ModifyStayBookingMutation} from './ModifyStayBooking';
import StayContext from '../../../../context/StayContext';
import {
    MODIFY_BOOKING,
    MODIFY_REQUEST,
    modifyBookingRequestFailed,
    modifyBookingRequestPresented,
    modifyBookingRequestSelected,
    modifyBookingRequestSubmitted,
    modifyBookingRequestSucceeded,
    TRIP_DETAILS,
} from '../../../../../utils/EDAP/ConversationDetails';

import ModifyRequest from './ModifyRequest/ModifyRequest';
import ModifyConfirmation from './ModifyConfirmation/ModifyConfirmation';

export function ModifyBookingModal({
    buttonLabel,
    modifyStayBooking,
    policyType,
    translations,
}) {
    const experienceType = MODIFY_REQUEST;
    const [isOpen, setIsOpen] = useState(false);
    const {
        stay: {
            booking: {
                arrival,
                departure,
                guests: {numberOfAdults, numberOfChildren},
                reservationUuid: reservationUUID,
            },
            conversation: {id: conversationId},
        },
    } = useContext(StayContext);
    const [isConfirmation, setIsConfirmation] = useState(false);
    const [isErrorOccurred, setIsErrorOccurred] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isAvailabilityLoading, setIsAvailabilityLoading] = useState(true);
    const [requestedFields, setRequestedFields] = useState({});

    function onOpenModal() {
        setIsConfirmation(false);
        if (!isOpen) {
            modifyBookingRequestSelected({
                actionLocation: TRIP_DETAILS,
                experienceType,
                reservationUUID,
            });
        }
        setIsOpen(true);
    }

    function onCloseModal() {
        setIsErrorOccurred(false);
        setIsOpen(false);
    }

    function onModalButtonEnter() {
        modifyBookingRequestPresented({
            actionLocation: TRIP_DETAILS,
            experienceType,
            reservationUUID,
        });
    }

    function submitRequest(updateBookingRequest) {
        setIsLoading(true);

        const fields = {};
        const eventFields = {
            actionLocation: MODIFY_BOOKING,
            experienceType,
            oldAdultCount: `${numberOfAdults}`,
            newAdultCount: `${numberOfAdults}`,
            oldChildCount: `${numberOfChildren}`,
            newChildCount: `${numberOfChildren}`,
            oldStartDate: arrival,
            oldEndDate: departure,
            newStartDate: arrival,
            newEndDate: departure,
            reservationUUID,
        };
        const {newRequestedPeriodOfStay, newRequestedGuests, notes} =
            updateBookingRequest;
        if (newRequestedPeriodOfStay) {
            const {checkInDate, checkOutDate} = newRequestedPeriodOfStay;
            fields.checkInDate = checkInDate;
            fields.checkOutDate = checkOutDate;

            eventFields.newStartDate = checkInDate;
            eventFields.newEndDate = checkOutDate;
        }
        if (newRequestedGuests) {
            const {adults, children} = newRequestedGuests;
            fields.adults = adults;
            fields.children = children;

            eventFields.newAdultCount = adults;
            eventFields.newChildCount = children;
        }
        if (notes) {
            fields.notes = notes;
        }

        setRequestedFields({...fields});
        modifyBookingRequestSubmitted({...eventFields});

        modifyStayBooking({
            variables: {conversationId, updateBookingRequest},
        })
            .then(() => {
                modifyBookingRequestSucceeded({...eventFields});
                setIsErrorOccurred(false);
                setIsConfirmation(true);
            })
            .catch(() => {
                modifyBookingRequestFailed({
                    actionLocation: MODIFY_BOOKING,
                    experienceType,
                    reservationUUID,
                });
                setIsErrorOccurred(true);
            })
            .then(() => {
                setIsLoading(false);
            });
    }

    return (
        <div className="ModifyBookingModal">
            <Waypoint onEnter={onModalButtonEnter}>
                <ButtonPrimary label={buttonLabel} onClick={onOpenModal} />
            </Waypoint>
            <Modal
                id="modify-booking-modal"
                data-testid="modify-booking-modal"
                isOpen={isOpen}
                isLoading={isLoading || isAvailabilityLoading}
                onClose={onCloseModal}
            >
                {!isConfirmation && (
                    <ModifyRequest
                        buttonLabel={buttonLabel}
                        conversationId={conversationId}
                        isErrorOccurred={isErrorOccurred}
                        policyType={policyType}
                        submitRequest={submitRequest}
                        translations={translations}
                        setIsAvailabilityLoading={setIsAvailabilityLoading}
                    />
                )}
                {isConfirmation && (
                    <ModifyConfirmation
                        onCloseModal={onCloseModal}
                        requestedFields={requestedFields}
                        translations={translations}
                    />
                )}
            </Modal>
        </div>
    );
}

ModifyBookingModal.propTypes = {
    buttonLabel: PropTypes.string,
    modifyStayBooking: PropTypes.func,
    policyType: PropTypes.string,
    translations: PropTypes.shape({}),
};

export default compose(
    graphql(ModifyStayBookingMutation, {name: 'modifyStayBooking'})
)(ModifyBookingModal);
